import React, { useCallback, useEffect, useContext, useState } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import ButtonSubmitFormikPermission from "../../../components/UI/Button/ButtonSubmitFormikPermission";
import CheckBoxFormik from "../../../components/UI/Checkbox/CheckBoxFormik";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import OverlayInfoAdicional from "../../../components/UI/Overlays/OverlayInfoAdicional";
import SelectRoteiroAcaoFiscal from "../../../components/UI/SelectInput/SelectRoteiroAcaoFiscalFormik";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import { AuthContext } from "../../../context/AuthContext";
import funcionalidades from "../../../context/AuthContext.funcionalidades";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";

const validationSchema = Yup.object().shape({
  codigo: Yup.string().default(""),
  descricao: Yup.string()
    .ensure()
    .default("")
    .required("Informe o nome")
    .min(1, "Nome muito curto")
    .max(100, "Nome muito longo"),
  sigla: Yup.string()
    .ensure()
    .default("")
    .required("Informe a sigla")
    .min(3, "Deve possuir pelo menos 3 caracteres")
    .max(10, "Deve possuir no máximo 10 caracteres"),
  sequencialRoteiro: Yup.string().default("").required("Informe o roteiro"),
  qtdeMaximaDias: Yup.number()
    .required("Informe a quantidade máxima de dias da OS")
    .positive()
    .integer()
    .min(1, "Valor mínimo deve ser 1")
    .max(999, "Valor máximo deve ser 999"),
  ativo: Yup.boolean().default(true),
});

const initialValues = {};
validationSchema._nodes.forEach((key) => {
  initialValues[key] = "";
});

const FormTipoAcaoFiscal = ({ history, location }) => {
  const { state } = location;
  const { isAllowed } = useContext(AuthContext);
  const [dadosInfo, setDadosInfo] = useState({});
  const { request: requestForm, data: dataForm, errors: errorForm, isLoading } = useApiClient();

  const handleSubmit = useCallback(
    (values) => {
      if (values.codigo) requestForm(apiRoutes.fiscalizacao.parametrizacao.alteraTipoAcaoFiscal, values);
      else requestForm(apiRoutes.fiscalizacao.parametrizacao.insereTipoAcaoFiscal, values);
    },
    [requestForm],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { setSubmitting, setErrors, isSubmitting, isValid } = formik;

  useEffect(() => {
    if ((dataForm || errorForm) && isSubmitting && !isLoading) formik.setSubmitting(false);

    if (errorForm && isValid && !isLoading) formik.setErrors({ ...formik.errors, ...errorForm });
  }, [formik, setSubmitting, setErrors, isSubmitting, isValid, dataForm, errorForm, isLoading]);

  useEffect(() => {
    if (!dataForm && !errorForm) return;

    if (dataForm) {
      toast.success("Tipo da Ação Fiscal Salvo com Sucesso");
      history.push("/app/tipoAcaoFiscal");
    }
  }, [dataForm, errorForm, history]);

  // Carregar item
  const { request: tipoRequest, data: tipoData, errors: tipoErrors, isLoading: tipoIsLoading } = useApiClient();

  useEffect(() => {
    if (state && state.itemKey) {
      tipoRequest(apiRoutes.fiscalizacao.parametrizacao.consultaTipoAcaoFiscal, { id: state.itemKey });
      delete state.itemKey;
    }
  }, [tipoRequest, state]);

  useEffect(() => {
    if ((!tipoData && !tipoErrors) || tipoIsLoading) return;

    if (!formik.values.codigo && tipoData && tipoData.result.length) {
      const info = {
        codigo: tipoData?.result[0]?.codigo,
        data: {
          documentoInclusao: tipoData?.result[0]?.usuarioInclusao,
          nomeFormalInclusao: tipoData?.result[0]?.nomeFormalInclusao,
          nomeFormalAlteracao: tipoData?.result[0]?.nomeFormalAlteracao,
          documentoAlteracao: tipoData?.result[0]?.usuarioAlteracao,
          dataInclusao: tipoData?.result[0]?.dataInclusao,
          dataAlteracao: tipoData?.result[0]?.dataAlteracao,
        },
      };

      setDadosInfo(info);

      formik.setValues({ ...tipoData.result[0] });
    }

    if (tipoErrors) {
      toast.error(tipoErrors.message);
    }
  }, [formik, tipoData, tipoErrors, tipoIsLoading]);

  return (
    <>
      {/* Cabeçalho da Página */}
      <h1 className="h3 mb-4 text-gray-800">Tipo de Ação Fiscal</h1>
      <div className="row">
        <div className="col-lg-12">
          {/* Conteúdo personalizado (card) */}
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1">
                    {formik.values.codigo ? "Alteração do Tipo de Ação Fiscal" : "Inclusão do Tipo de Ação Fiscal"}
                  </h6>
                </Col>
                <Col sm="1" className="text-right">
                  <OverlayInfoAdicional data={dadosInfo} keyFieldName="codigo" />
                </Col>
              </Row>
            </div>
            <div className="card-body">
              <FormAlert formik={formik} data={dataForm} errors={errorForm} isLoading={isLoading || tipoIsLoading} />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <fieldset
                  disabled={
                    formik.values.codigo
                      ? !isAllowed(funcionalidades.tipoAcaoFiscal.alterar)
                      : !isAllowed(funcionalidades.tipoAcaoFiscal.inserir)
                  }
                >
                  <Row>
                    <Form.Group as={Col}>
                      <Form.Label>Nome</Form.Label>
                      <TextInputFormik formik={formik} field="descricao" maxLength="100" />
                    </Form.Group>
                    {formik.values.codigo ? (
                      <Form.Group as={Col} sm={12} md={3}>
                        <Form.Label> </Form.Label>
                        <CheckBoxFormik formik={formik} field="ativo" label="Ativo" />
                      </Form.Group>
                    ) : null}
                  </Row>
                  <Row>
                    <Form.Group as={Col} sm={12} md={3}>
                      <Form.Label>Sigla</Form.Label>
                      <TextInputFormik formik={formik} field="sigla" maxLength="10" />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={6}>
                      <Form.Label>Roteiro</Form.Label>
                      <SelectRoteiroAcaoFiscal formik={formik} field="sequencialRoteiro" />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={3}>
                      <Form.Label>N° Máximo de Dias da OS</Form.Label>
                      <TextInputFormik formik={formik} field="qtdeMaximaDias" maxLength="3" type="number" />
                    </Form.Group>
                  </Row>
                </fieldset>
                <Row className="mt-2">
                  <Col>
                    {formik.isSubmitting ? (
                      <TextSpinner text="Registrando Tipo de Ação Fiscal..." />
                    ) : (
                      <ButtonSubmitFormikPermission
                        permission={
                          formik.values.codigo
                            ? funcionalidades.tipoAcaoFiscal.alterar
                            : funcionalidades.tipoAcaoFiscal.inserir
                        }
                        disabled={formik.isSubmitting}
                        name="Salvar"
                      />
                    )}
                    {"    "}
                    {!formik.isSubmitting && !formik.values.codigo && (
                      <Button variant="light" className="mr-3" onClick={formik.handleReset}>
                        Limpar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="danger" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
      {/* fim da row */}
    </>
  );
};

export default FormTipoAcaoFiscal;
